import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";

const ARCGIS_URL = process.env.REACT_APP_ARCGIS_URL || "https://mapas.terrenos.pr.gov/server/rest/services/InventariosTerrenos/ADM_Terrenos/FeatureServer/0";

const STATUS_COLOR: Record<string, string> = {
  "0": "#22c55e",
  "1": "#3b82f6",
  "2": "#ef4444",
  "3": "#f59e0b",
};

const STATUS_LABEL: Record<string, string> = {
  "0": "Disponible",
  "1": "Arrendada",
  "2": "Vendida",
  "3": "En Proceso",
};

interface ArcGISFeature {
  type: string;
  geometry: { type: string; coordinates: number[][][] };
  properties: {
    IDFINCA: string;
    FINCA: string;
    MUNICIPIO: string;
    DISPONIBILIDAD: number | null;
    ARRENDADO_A: string;
    CABIDA_ARRENDADA_CDAS: number | null;
    CABIDA_ESCRITURA_CDAS: number | null;
    CUERDAS_MAPA: number | null;
    NUMERO_CATASTRO: string;
    [key: string]: string | number | null | undefined;
  };
}

interface GeoJSONData {
  type: string;
  features: ArcGISFeature[];
}

// Must be inside MapContainer — adjusts view whenever geoData changes
const FitBounds: React.FC<{ geoData: GeoJSONData | null }> = ({ geoData }) => {
  const map = useMap();
  useEffect(() => {
    if (!geoData || geoData.features.length === 0) {
      map.setView([18.22, -66.59], 9);
      return;
    }
    try {
      const bounds = L.geoJSON(geoData as unknown as GeoJSON.FeatureCollection).getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [48, 48] });
    } catch {}
  }, [geoData, map]);
  return null;
};

// Flies to the selected parcel's bounds when selectedParcelId changes
const ZoomToSelected: React.FC<{ geoData: GeoJSONData | null; selectedParcelId?: string }> = ({
  geoData,
  selectedParcelId,
}) => {
  const map = useMap();
  useEffect(() => {
    if (!selectedParcelId || !geoData) return;
    const feature = geoData.features.find(
      (f) => f.properties.FINCA === selectedParcelId || f.properties.IDFINCA === selectedParcelId
    );
    if (!feature) return;
    try {
      const bounds = L.geoJSON(feature as unknown as GeoJSON.Feature).getBounds();
      if (bounds.isValid()) map.flyToBounds(bounds, { padding: [60, 60], maxZoom: 16, duration: 0.8 });
    } catch {}
  }, [selectedParcelId, geoData, map]);
  return null;
};

interface Props {
  parcelIds: string[];
  onParcelClick?: (parcelId: string) => void;
  height?: string;
  selectedParcelId?: string;
}

export const ArcGISSearchMap: React.FC<Props> = ({ parcelIds, onParcelClick, height = "600px", selectedParcelId }) => {
  const [geoData, setGeoData] = useState<GeoJSONData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (parcelIds.length === 0) {
      setGeoData(null);
      return;
    }

    let cancelled = false;
    const fetchParcels = async () => {
      setLoading(true);
      try {
        const ids = parcelIds.map(id => `'${id}'`).join(",");
        const where = `FINCA IN (${ids})`;
        const url = `${ARCGIS_URL}/query?where=${encodeURIComponent(where)}&outFields=IDFINCA,FINCA,MUNICIPIO,DISPONIBILIDAD,ARRENDADO_A,CABIDA_ARRENDADA_CDAS,CABIDA_ESCRITURA_CDAS,CUERDAS_MAPA,NUMERO_CATASTRO&outSR=4326&f=geojson`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (cancelled) return;
        if (data.error) {
          console.error("[ArcGIS] search map error:", data.error.message);
          setGeoData(null);
        } else {
          setGeoData(data.features?.length > 0 ? (data as GeoJSONData) : null);
        }
      } catch (err) {
        if (!cancelled) console.error("[ArcGIS] fetch error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchParcels();
    return () => { cancelled = true; };
  }, [parcelIds]);

  // Global handler for popup "Ver detalle" link (Leaflet popups don't support React events)
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__arcgisSearchClick__ =
      (fincaId: string) => onParcelClick?.(fincaId);
    return () => {
      delete (window as unknown as Record<string, unknown>).__arcgisSearchClick__;
    };
  }, [onParcelClick]);

  const styleFeature = (feature: ArcGISFeature | undefined) => {
    const disp = String(feature?.properties?.DISPONIBILIDAD ?? "0");
    return {
      color: STATUS_COLOR[disp] ?? "#6b7280",
      weight: 2,
      opacity: 0.9,
      fillColor: STATUS_COLOR[disp] ?? "#6b7280",
      fillOpacity: 0.3,
    };
  };

  const onEachFeature = (feature: ArcGISFeature, layer: L.Layer) => {
    const p = feature.properties;
    const disp = String(p.DISPONIBILIDAD ?? "0");
    const cabida = p.CUERDAS_MAPA ?? p.CABIDA_ESCRITURA_CDAS ?? p.CABIDA_ARRENDADA_CDAS;
    const color = STATUS_COLOR[disp] ?? "#6b7280";

    (layer as L.Path).bindPopup(`
      <div style="font-family:Segoe UI,sans-serif;font-size:13px;min-width:180px;">
        <div style="font-weight:700;font-size:14px;margin-bottom:3px;">${p.IDFINCA}</div>
        <div style="color:#666;margin-bottom:6px;">${p.MUNICIPIO}</div>
        <div style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;color:#fff;background:${color};margin-bottom:8px;">
          ${STATUS_LABEL[disp] ?? "—"}
        </div>
        ${cabida ? `<div style="font-size:12px;color:#555;margin-bottom:6px;">${cabida.toFixed(2)} cuerdas</div>` : ""}
        ${onParcelClick
          ? `<a href="#" onclick="window.__arcgisSearchClick__('${p.FINCA}');return false;"
               style="color:#2563eb;font-size:12px;font-weight:500;text-decoration:none;">
               Ver detalle →
             </a>`
          : ""}
      </div>
    `);

    (layer as L.Path).on("click", () => onParcelClick?.(p.FINCA));
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200" style={{ height: `calc(${height} + 36px)` }}>
      <div style={{ height }}>
        <MapContainer
          center={[18.22, -66.59]}
          zoom={9}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
          {geoData && (
            <GeoJSON
              key={parcelIds.join(",")}
              data={geoData as unknown as GeoJSON.FeatureCollection}
              style={f => styleFeature(f as unknown as ArcGISFeature)}
              onEachFeature={(f, l) => onEachFeature(f as unknown as ArcGISFeature, l)}
            />
          )}
          <FitBounds geoData={geoData} />
          <ZoomToSelected geoData={geoData} selectedParcelId={selectedParcelId} />
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-white border-t text-xs text-gray-500">
        {loading
          ? <span className="text-gray-400 italic">Cargando mapa...</span>
          : geoData
            ? <span className="font-medium text-gray-700">{geoData.features.length} parcela{geoData.features.length !== 1 ? "s" : ""} en mapa</span>
            : <span className="text-gray-400">Realice una búsqueda para ver parcelas en el mapa</span>}
        {geoData && <span className="text-gray-300">|</span>}
        {geoData && Object.entries(STATUS_COLOR).map(([key, color]) => (
          <div key={key} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ background: color, opacity: 0.7 }} />
            <span>{STATUS_LABEL[key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
